/**
 * N3 · Competência 3 (Geometria) · **eixo cognitivo III** · task-model H2.15.K01
 *
 *   "Quais destes polígonos regulares pavimentam o plano sozinhos?"
 *
 * A habilidade H2.15 pede *avaliar a viabilidade da pavimentação do plano por figuras
 * planas*, e a condição é de uma linha só: o ângulo interno precisa caber um número
 * **inteiro** de vezes em 360°. Triângulo (60°), quadrado (90°) e hexágono (120°)
 * cabem; pentágono (108°) e octógono (135°) não, e é por isso que não existe calçada de
 * pentágonos regulares.
 *
 * O EXPLORADOR RECEBE UM ÂNGULO, E NÃO UM POLÍGONO — a decisão que faz o item medir
 * alguma coisa. Se ele recebesse o polígono, bastaria percorrer a lista para ter as seis
 * respostas de graça. Recebendo o ângulo, e andando de dez em dez, ele ensina a regra
 * com os ângulos redondos e deixa **108° e 135° fora de alcance**: justamente os dois
 * que a intuição erra, e os dois que os erros com nome perseguem.
 *
 * O ângulo interno de cada polígono vem escrito na figura. O construto aqui é decidir se
 * o ângulo fecha a volta; calcular (n−2)·180/n é outra habilidade, e já tem itens.
 */

import { formatBR } from '../../core/num.ts';
import { de, oS, type NomeId } from '../../core/pt.ts';
import type { Rng } from '../../core/rng.ts';
import {
  anguloInterno,
  describeLadrilho,
  ladrilhoAoRedor,
  poligonoRegular,
} from '../../render/ladrilho.ts';
import type { Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  LADR_MAIS_LADOS_MELHOR,
  LADR_PENTAGONO,
  LADR_SO_ANGULO_RETO,
  LADR_TODOS_REGULARES,
  LADR_TRIANGULO_DE_FORA,
} from '../../schema/errors.ts';
import type { ItemTemplate, Regiao } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

const NOMES: Readonly<Record<number, string>> = {
  3: 'triângulo',
  4: 'quadrado',
  5: 'pentágono',
  6: 'hexágono',
  8: 'octógono',
  9: 'eneágono',
  10: 'decágono',
  12: 'dodecágono',
};

/** Cabe em 360° um número inteiro de vezes? */
const pavimenta = (lados: number): boolean =>
  Math.abs(360 / anguloInterno(lados) - Math.round(360 / anguloInterno(lados))) < 1e-9;

interface Contexto {
  readonly texto: string;
  /**
   * A superfície a cobrir, como chave do léxico. Era o texto "a praça", e o comando o
   * completava com "inteiro" fixo: "cobrir a praça inteiro". As dicas e a resolução,
   * por sua vez, diziam "um ponto do chão" em todas as variantes — também na da parede.
   */
  readonly onde: NomeId;
}

const CONTEXTOS: readonly Contexto[] = [
  {
    texto:
      'Uma fábrica de pisos quer lançar uma linha de ladrilhos com uma forma só: peças todas iguais, encostadas umas nas outras, sem sobrar buraco e sem uma peça montar na outra.',
    onde: 'chao',
  },
  {
    texto:
      'Um artesão vai cobrir uma parede com pastilhas de uma forma só: peças todas iguais, encostadas umas nas outras, sem sobrar buraco e sem uma peça montar na outra.',
    onde: 'parede',
  },
  {
    texto:
      'Uma praça será pavimentada com blocos de uma forma só: peças todas iguais, encostadas umas nas outras, sem sobrar buraco e sem uma peça montar na outra.',
    onde: 'praca',
  },
];

export interface Params {
  readonly contexto: number;
  /** Os polígonos oferecidos, pelo número de lados, na ordem em que aparecem. */
  readonly lados: readonly number[];
}

/** O explorador anda de dez em dez — é o que mantém 108° e 135° fora de alcance. */
const PASSO_ANGULO = 10;
const ANG_MIN = 60;
const ANG_MAX = 170;

function drawParams(rng: Rng): Params {
  // Sempre os três que pavimentam, mais três que não pavimentam: com um número variável
  // de chaves, a contagem viraria pista.
  const bons = [3, 4, 6];
  const maus = rng.shuffle([5, 8, 9, 10, 12]).slice(0, 3);
  return {
    contexto: rng.int(0, CONTEXTOS.length - 1),
    lados: rng.shuffle([...bons, ...maus]),
  };
}

const COL = 3;
const CEL = 100;
const RAIO = 34;

const centroDe = (i: number): { x: number; y: number } => ({
  x: (i % COL) * CEL + CEL / 2,
  y: -Math.floor(i / COL) * CEL - CEL / 2,
});

export const ladrilhar: ItemTemplate<Params, null> = {
  id: templateId('h2-15-K01-ladrilhar'),
  version: 1,
  taskModel: taskModelCode('H2.15.K01'),
  skills: [skillCode('H2.15')],
  axis: 'III',
  level: 'N3',
  title: 'Quais polígonos pavimentam o plano',
  responseModel: 'GPCM',
  difficultySources: ['steps.2', 'two-supports'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // O pentágono é o engano clássico; com ele na lista, o item separa melhor.
    if (p.lados.includes(5)) fontes.push('distractor-proximity.high');
    return fontes;
  },
  validate(_b, p) {
    if (p.lados.filter(pavimenta).length !== 3)
      return 'a quantidade de polígonos que pavimentam mudou';
    if (new Set(p.lados).size !== p.lados.length) return 'polígono repetido na lista';
    return null;
  },
  build(p) {
    const ctx = CONTEXTOS[p.contexto] as Contexto;

    const backdrop: Shape[] = [];
    const regioes: Regiao[] = [];
    p.lados.forEach((n, i) => {
      const c = centroDe(i);
      const forma = poligonoRegular(n, c, RAIO);
      backdrop.push({ t: 'poly', pts: forma, closed: true, role: 'given', fill: 'given-fill' });
      backdrop.push({
        t: 'label',
        at: { x: c.x, y: c.y - CEL * 0.42 },
        text: [txt(`${NOMES[n]} · ${anguloInterno(n)}°`)],
        anchor: 'c',
        emphasis: 'tick',
      });
      regioes.push({
        id: String(n),
        forma,
        rotulo: NOMES[n] as string,
        nome: `${NOMES[n]}, de ângulo interno ${anguloInterno(n)} graus`,
      });
    });

    const chave = p.lados.filter(pavimenta).map(String);
    const marcou = (n: number) => (sel: readonly string[]) => sel.includes(String(n));
    const temMuitosLados = (sel: readonly string[]): boolean =>
      sel.some((s) => Number(s) >= 8 && !pavimenta(Number(s)));

    const analisar = (sel: readonly string[]): RichText => {
      const partes: string[] = [];
      for (const n of p.lados) {
        const dentro = sel.includes(String(n));
        const a = anguloInterno(n);
        if (dentro && !pavimenta(n)) {
          partes.push(`O ${NOMES[n]} não pavimenta: 360 ÷ ${a} não dá inteiro, e sobra fresta.`);
        }
        if (!dentro && pavimenta(n)) {
          partes.push(
            `O ${NOMES[n]} pavimenta — ${360 / a} peças de ${a}° fecham a volta — e ficou sem marcar.`,
          );
        }
      }
      return [txt(partes.join(' '))];
    };

    const descricao =
      `Seis polígonos regulares, um em cada quadro, com o nome e o ângulo interno escritos embaixo: ` +
      p.lados.map((n) => `${NOMES[n]}, ${anguloInterno(n)} graus`).join('; ') +
      '. Cada um pode ser marcado.';

    const linhas = Math.ceil(p.lados.length / COL);

    return {
      stem: [
        txt(ctx.texto),
        br,
        txt(
          'Abaixo, use o explorador: ele encosta peças iguais de um mesmo ângulo em volta de um ponto.',
        ),
      ],
      comando: [
        txt(`Marque os polígonos que, sozinhos, conseguem cobrir ${oS(ctx.onde).adj('inteiro')}.`),
      ],
      stimuli: [
        {
          kind: 'manipulable',
          motion: 'interactive',
          audio: { need: 'none' },
          id: 'explorador',
          alt: 'Peças iguais de um mesmo ângulo, encostadas em volta de um ponto; o ângulo das peças é escolhido no seletor.',
          controls: [
            {
              id: 'angulo',
              label: [txt('Ângulo de cada peça')],
              min: ANG_MIN,
              max: ANG_MAX,
              step: PASSO_ANGULO,
              initial: 70,
              format: (v) => `${v}°`,
            },
          ],
          frame: (v) => {
            const angulo = v['angulo'] ?? 70;
            return ladrilhoAoRedor({
              angulo,
              title: 'Peças de mesmo ângulo em volta de um ponto',
              desc: describeLadrilho(angulo),
            });
          },
          quadroImpresso: { angulo: 120 },
        },
      ],
      interaction: {
        kind: 'region-select',
        descricao,
        backdrop,
        viewBox: { x: -8, y: -linhas * CEL - 8, w: COL * CEL + 16, h: linhas * CEL + 16 },
        regioes,
        oQue: 'poligono',
        chave,
        erros: [],
        diagnosticos: [
          { error: LADR_TODOS_REGULARES, test: (sel) => sel.length === p.lados.length },
          { error: LADR_SO_ANGULO_RETO, test: (sel) => sel.length === 1 && sel[0] === '4' },
          { error: LADR_PENTAGONO, test: marcou(5) },
          { error: LADR_MAIS_LADOS_MELHOR, test: temMuitosLados },
          { error: LADR_TRIANGULO_DE_FORA, test: (sel) => !sel.includes('3') },
        ],
        analisar,
      },
      dicas: [
        [
          txt(
            `Junte peças iguais em volta de um ponto ${de(oS(ctx.onde))}. Para não sobrar buraco nem montar uma na outra, os ângulos que se encontram nesse ponto precisam somar `,
          ),
          strong('360°'),
          txt(' exatos.'),
        ],
        [
          txt('Então a pergunta é: o ângulo do polígono cabe um número '),
          strong('inteiro'),
          txt(
            ' de vezes em 360°? Experimente no explorador com 60°, 90° e 120°, e depois faça a conta para os outros.',
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `Em volta de qualquer ponto ${de(oS(ctx.onde).adj('coberto'))}, os ângulos das peças que se encontram ali precisam somar 360° exatos — nem menos (sobra fresta), nem mais (uma peça monta na outra).`,
        ),
        br,
        txt(
          'Como as peças são todas iguais, isso quer dizer que o ângulo interno precisa caber um número inteiro de vezes em 360°:',
        ),
        br,
        txt(
          p.lados
            .map((n) => {
              const a = anguloInterno(n);
              const q = 360 / a;
              return pavimenta(n)
                ? `${NOMES[n]}, ${a}°: 360 ÷ ${a} = ${q}, inteiro — pavimenta`
                : `${NOMES[n]}, ${a}°: 360 ÷ ${a} ≈ ${formatBR(Math.round(q * 100) / 100, 2)}, não é inteiro — não pavimenta`;
            })
            .join('; ') + '.',
        ),
        br,
        txt(
          'É por isso que existem pisos de hexágonos e não existem pisos de pentágonos regulares.',
        ),
      ],
      declared: 'media',
    };
  },
};
